import React, { useState } from 'react'

import { TopFilters as TopFiltersWrapper } from '@app/components'
import { LntInput, LntSelect } from '@app/components/base'

import { FILTERS_PARAMS_KEYS } from '@app/api/constants'
import { FILTERS_PLACEHOLDERS } from '@app/constants/filters'
import { CATALOG_KEY } from '@app/constants/queryKeys'
import { SIZE } from '@app/constants'

import {
  getCategories,
  getCommodityGroups,
  getPurchasingGroups,
  getSubcategories,
  getTrends
} from '@app/api/routes/catalog'

import { stringRegexReplace } from '@app/utils/string'

import { useFiltersSearch } from '@app/hooks'

import { getFilterFromEntities, getFilterFromEntitiesToLabelsIds } from '@app/utils/entitiesAdapters'

import { commonSelectProps, confirmedOptions, isMatchedWithProductOptions, singleSelectProps } from './config'

import type { ChangeEvent } from 'react'
import type { TopFilterProps } from './types'
import type { FiltersParams } from '@app/types/filter'

const updateFiltersByKey = (prev: FiltersParams | null, key: string, value: unknown): object => {
  if (!prev) return { [key]: value }
  return { ...prev, [key]: value }
}

export const TopFilters: React.FC<TopFilterProps> = ({ initialFilters, onSearch }) => {
  const {
    filters,
    canResetFilters,
    setFilters,
    handleClearFilters: clearFilters,
    handleSearch: submitFilters,
    handleSelectFilterChange,
    handleInputFilterChange
  } = useFiltersSearch({ initialFilters, onSearch })

  const [parsedProductCodeValue, setParsedProductCodeValue] = useState(filters?.productCodes)

  const onChangeInputProductCode = (event: ChangeEvent<HTMLInputElement>): void => {
    const value = event.target.value

    setParsedProductCodeValue(value)

    const REGEXP_PATTERN = /\D+/g
    const parsedString = stringRegexReplace(value, REGEXP_PATTERN)

    setFilters(prev => updateFiltersByKey(prev, FILTERS_PARAMS_KEYS.productCodes, parsedString))
  }

  const handleClearFilters = (): void => {
    clearFilters()
    setParsedProductCodeValue('')
  }

  const handleSearch = (): void => {
    submitFilters()
    setParsedProductCodeValue(filters?.productCodes)
  }

  return (
    <TopFiltersWrapper
      canReset={ canResetFilters }
      onClear={ handleClearFilters }
      onSearch={ handleSearch }
    >
      <LntInput
        value={ filters.name }
        onChange={ event => handleInputFilterChange(FILTERS_PARAMS_KEYS.name, event) }
        placeholder={ FILTERS_PLACEHOLDERS.name }
        size={ SIZE.middle }
      />

      <LntSelect
        { ...commonSelectProps }
        value={ filters.trendIds }
        onChange={ value => handleSelectFilterChange(FILTERS_PARAMS_KEYS.trendIds, value) }
        placeholder={ FILTERS_PLACEHOLDERS.trendsFilter }
        querySelect={ {
          queryKey: [CATALOG_KEY.trendsItems],
          queryFn: getTrends,
          select: getFilterFromEntitiesToLabelsIds
        } }
      />

      <LntSelect
        { ...commonSelectProps }
        value={ filters.purchasingGroupIds }
        onChange={ value => handleSelectFilterChange(FILTERS_PARAMS_KEYS.purchasingGroupIds, value) }
        placeholder={ FILTERS_PLACEHOLDERS.purchasingGroupsFilter }
        querySelect={ {
          queryKey: [CATALOG_KEY.purchasingGroupsItems],
          queryFn: getPurchasingGroups,
          select: getFilterFromEntitiesToLabelsIds
        } }
      />

      <LntSelect
        { ...commonSelectProps }
        value={ filters.commodityGroupIds }
        onChange={ value => handleSelectFilterChange(FILTERS_PARAMS_KEYS.commodityGroupIds, value) }
        placeholder={ FILTERS_PLACEHOLDERS.commodityGroupsFilter }
        querySelect={ {
          queryKey: [CATALOG_KEY.commodityGroupsItems],
          queryFn: getCommodityGroups,
          select: getFilterFromEntitiesToLabelsIds
        } }
      />

      <LntSelect
        { ...commonSelectProps }
        value={ filters.categoriesIds }
        onChange={ value => handleSelectFilterChange(FILTERS_PARAMS_KEYS.categoriesIds, value) }
        placeholder={ FILTERS_PLACEHOLDERS.categoriesFilter }
        querySelect={ {
          queryKey: [CATALOG_KEY.categoriesItems],
          queryFn: getCategories,
          select: getFilterFromEntitiesToLabelsIds
        } }
      />

      <LntSelect
        { ...commonSelectProps }
        value={ filters.subcategoriesIds }
        onChange={ value => handleSelectFilterChange(FILTERS_PARAMS_KEYS.subcategoriesIds, value) }
        placeholder={ FILTERS_PLACEHOLDERS.subcategoriesFilter }
        querySelect={ {
          queryKey: [CATALOG_KEY.subcategoriesItems],
          queryFn: getSubcategories,
          select: getFilterFromEntitiesToLabelsIds
        } }
      />

      <LntSelect
        { ...commonSelectProps }
        value={ filters.subcategoriesIds }
        onChange={ value => handleSelectFilterChange(FILTERS_PARAMS_KEYS.subcategoriesIds, value) }
        placeholder={ FILTERS_PLACEHOLDERS.subcategoriesFilter }
        querySelect={ {
          queryKey: [CATALOG_KEY.subcategoriesItems],
          queryFn: getSubcategories,
          select: getFilterFromEntities
        } }
      />

      <LntSelect
        { ...singleSelectProps }
        placeholder={ FILTERS_PLACEHOLDERS.isConfirmed }
        value={ filters.isConfirmed }
        options={ confirmedOptions }
        onChange={ value => handleSelectFilterChange(FILTERS_PARAMS_KEYS.isConfirmed, value) }
      />

      <LntSelect
        { ...singleSelectProps }
        placeholder={ FILTERS_PLACEHOLDERS.isMatchedWithProduct }
        value={ filters.isMatchedWithProduct }
        options={ isMatchedWithProductOptions }
        onChange={ value => handleSelectFilterChange(FILTERS_PARAMS_KEYS.isMatchedWithProduct, value) }
      />

      <LntInput
        value={ parsedProductCodeValue }
        onChange={ event => onChangeInputProductCode(event) }
        placeholder={ FILTERS_PLACEHOLDERS.productCodeLenta }
        size={ SIZE.middle }
      />
    </TopFiltersWrapper>
  )
}
